import {
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from "@chakra-ui/react";
import { DrawdownFund } from "features/drawdown/DrawdownFund";
import React from "react";
import { Trans, useTranslation } from "react-i18next";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { decimalZero } from "shared/utils/DecimalUtils";
import { formatPercentage } from "shared/utils/formattingUtils";
import { formatMoney } from "shared/utils/MoneyUtils";

export interface FeePostApplyOffsetsAllocationDetailsDrawerProps
  extends FeePostApplyOffsetsAllocationDetailsDrawerCommonProps {
  onCloseComplete: () => void;
}

interface FeePostApplyOffsetsAllocationDetailsDrawerCommonProps {
  feeCode: string;
  allocations: DrawdownFund[];
}

export const FeePostApplyOffsetsAllocationDetailsDrawer: React.FC<
  FeePostApplyOffsetsAllocationDetailsDrawerProps
> = ({ onCloseComplete, allocations, feeCode }) => {
  const { isOpen, onClose } = useDisclosure({ defaultIsOpen: true });
  const { t } = useTranslation(["common", "fees"]);

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      onCloseComplete={onCloseComplete}
      closeOnEsc={true}
      variant="floating"
      size="lg"
      closeOnOverlayClick={true}
    >
      <DrawerOverlay bg="transparent" />
      <DrawerContent>
        <DrawerHeader>
          {t("fees:applyOffsets.allocationDetails.title")}
        </DrawerHeader>
        <DrawerBody px="24px" mt="-8px">
          <Text mb="16px">
            <Trans
              ns="fees"
              i18nKey="applyOffsets.allocationDetails.feeCode"
              values={{ feeCode }}
            >
              <Text as="span" textStyle="smallBold" color="gray.500">
                Label
              </Text>
              Value
            </Trans>
          </Text>
          <TableBox isFullWidth={true}>
            <TableBoxBody>
              <TableContainer>
                <Table>
                  <Thead>
                    <Tr>
                      <Th>{t("common:entity")}</Th>
                      <Th>{t("common:basis")}</Th>
                      <Th>{t("common:percentage")}</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {allocations.map(
                      ({ id, basisAmount, ownershipPercentage, title }) => (
                        <Tr key={id}>
                          <Td>{title}</Td>
                          <Td>{formatMoney(basisAmount?.toString())}</Td>
                          <Td>
                            {formatPercentage(
                              ownershipPercentage?.mul(100) ?? decimalZero(),
                            )}
                          </Td>
                        </Tr>
                      ),
                    )}
                  </Tbody>
                </Table>
              </TableContainer>
            </TableBoxBody>
          </TableBox>
        </DrawerBody>
        <DrawerFooter>
          <Button colorScheme="secondary" onClick={onClose}>
            {t("common:close")}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
